import { Button } from '@/components/ui/button'
import {
  createFileRoute,
  redirect,
  useLocation,
  useRouter,
} from '@tanstack/react-router'
import { auth } from '@/lib/firebase'
import { useCallback, useEffect, useState } from 'react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  AlertTriangleIcon,
  Loader2,
  LinkIcon,
  ArrowLeft,
  CheckCircle,
  ChevronDown,
} from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { getIdTokenUsingQueryClient } from '@/data/queries'
import {
  linkWithPopup,
  sendPasswordResetEmail,
  signInWithPopup,
  signInWithEmailAndPassword as firebaseSignInWithEmailAndPassword,
  getMultiFactorResolver,
  type MultiFactorError,
  type MultiFactorResolver,
} from 'firebase/auth'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  InputOTPSeparator,
} from '@/components/ui/input-otp'
import { REGEXP_ONLY_DIGITS } from 'input-otp'
import { TotpMultiFactorGenerator } from 'firebase/auth'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  prominentProviders,
  dropdownProviders,
  getProviderConfig,
  type OAuthProviderConfig,
} from '@/lib/auth-providers'
import { LoginPasskeyButton } from '@/components/login/LoginPasskeyButton'

type LoginSearch = {
  redirectTo?: string
}

export const Route = createFileRoute('/login')({
  component: LoginPage,
  validateSearch(search: Record<string, unknown>): LoginSearch {
    return {
      redirectTo: search['redirectTo'] as string | undefined,
    }
  },
  beforeLoad: async ({ context, search }) => {
    const { queryClient } = context
    const authState = auth.currentUser
    if (authState && queryClient) {
      await getIdTokenUsingQueryClient({
        queryClient,
        authState,
      })

      console.debug('redirectTo', search.redirectTo)
      if (search.redirectTo) {
        throw redirect({ to: search.redirectTo })
      } else {
        throw redirect({ to: '/documents' })
      }
    }
  },
})

function LoginPage() {
  const router = useRouter()

  // Generic OAuth state
  const [loadingProviderId, setLoadingProviderId] = useState<string | null>(
    null,
  )
  const [oauthError, setOauthError] = useState<{
    providerId: string
    error: Error
  } | null>(null)

  const [loadingEmail, setLoadingEmail] = useState(false)
  const [errorEmail, setErrorEmail] = useState<Error | null>(null)
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(
    null,
  )
  const [showMfaChallenge, setShowMfaChallenge] = useState(false)
  const [mfaCode, setMfaCode] = useState('')
  const [mfaError, setMfaError] = useState<string | null>(null)
  const [mfaLoading, setMfaLoading] = useState(false)

  const location = useLocation()
  const redirectTo = location.search.redirectTo ?? '/documents'

  const [showSignInWithEmail, setShowSignInWithEmail] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('')
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false)
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false)
  const [forgotPasswordError, setForgotPasswordError] = useState<string | null>(
    null,
  )

  // Account linking state — stores provider ID (not display name)
  const [linkingState, setLinkingState] = useState<{
    isLinking: boolean
    providerEmail: string
    providerId: string
  }>({
    isLinking: false,
    providerEmail: '',
    providerId: '',
  })
  const [linkingLoading, setLinkingLoading] = useState(false)
  const [linkingError, setLinkingError] = useState<Error | null>(null)

  // Check for account-exists-with-different-credential error
  useEffect(() => {
    if (!oauthError) return
    const { error, providerId } = oauthError
    if (
      (error as any)?.code === 'auth/account-exists-with-different-credential'
    ) {
      const providerEmail =
        (error as any).customData?.email || (error as any).email
      if (providerEmail) {
        setLinkingState({
          isLinking: true,
          providerEmail,
          providerId,
        })
        setEmail(providerEmail)
        setShowSignInWithEmail(true)
      }
    }
  }, [oauthError])

  // Intercept MFA errors from OAuth sign-in
  useEffect(() => {
    if (
      oauthError &&
      (oauthError.error as any).code === 'auth/multi-factor-auth-required'
    ) {
      setMfaResolver(
        getMultiFactorResolver(
          auth,
          oauthError.error as unknown as MultiFactorError,
        ),
      )
      setShowMfaChallenge(true)
    }
  }, [oauthError])

  // Ensure email stays locked to provider email during linking
  useEffect(() => {
    if (
      linkingState.isLinking &&
      linkingState.providerEmail &&
      email !== linkingState.providerEmail
    ) {
      setEmail(linkingState.providerEmail)
    }
  }, [email, linkingState.isLinking, linkingState.providerEmail])

  const handleSignInWithProvider = useCallback(
    async (config: OAuthProviderConfig) => {
      if (linkingState.isLinking) return

      setLoadingProviderId(config.id)
      setOauthError(null)
      try {
        const provider = config.createProvider()
        const result = await signInWithPopup(auth, provider)
        if (result?.user) {
          router.navigate({ to: redirectTo })
        }
      } catch (error) {
        console.error('Login Error', error)
        setOauthError({ providerId: config.id, error: error as Error })
      } finally {
        setLoadingProviderId(null)
      }
    },
    [linkingState.isLinking, router, redirectTo],
  )

  const handleSignInWithEmail = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      setLinkingError(null)
      setErrorEmail(null)

      try {
        if (linkingState.isLinking) {
          // Link workflow: sign in first, then link with popup
          setLinkingLoading(true)

          try {
            const userCredential = await firebaseSignInWithEmailAndPassword(
              auth,
              email,
              password,
            )

            if (userCredential.user) {
              // Create fresh provider for linking using the registry
              const config = getProviderConfig(linkingState.providerId)
              if (!config) {
                setLinkingError(new Error('Unknown provider for linking.'))
                return
              }
              const provider = config.createProvider()

              // Set login hint for better UX
              if ('setCustomParameters' in provider) {
                ;(provider as any).setCustomParameters({
                  login_hint: linkingState.providerEmail,
                })
              }

              // Link with fresh OAuth popup
              await linkWithPopup(userCredential.user, provider)

              // Success - navigate to documents
              router.navigate({ to: redirectTo })
            }
          } catch (linkError: any) {
            if (linkError.code === 'auth/multi-factor-auth-required') {
              // MFA during linking is not supported — user must sign in directly
              const mfaError = new Error(
                'This account has two-factor authentication enabled. Please sign in directly with your email and password first.',
              )
              setLinkingError(mfaError)
            } else {
              console.error('Linking Error', linkError)
              setLinkingError(linkError as Error)
            }
          }
        } else {
          // Normal email/password login
          setLoadingEmail(true)
          try {
            await firebaseSignInWithEmailAndPassword(auth, email, password)
            router.navigate({ to: redirectTo })
          } catch (error: any) {
            if (error.code === 'auth/multi-factor-auth-required') {
              setMfaResolver(
                getMultiFactorResolver(auth, error as MultiFactorError),
              )
              setShowMfaChallenge(true)
            } else {
              setErrorEmail(error)
            }
          } finally {
            setLoadingEmail(false)
          }
        }
      } catch (error) {
        console.error('Login Error', error)
      } finally {
        setLinkingLoading(false)
      }
    },
    [
      linkingState.isLinking,
      linkingState.providerId,
      linkingState.providerEmail,
      email,
      password,
      router,
      redirectTo,
    ],
  )

  const handleCancelLinking = useCallback(() => {
    setLinkingState({
      isLinking: false,
      providerEmail: '',
      providerId: '',
    })
    setEmail('')
    setPassword('')
    setShowSignInWithEmail(false)
    setLinkingError(null)
  }, [])

  const handleForgotPassword = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      setForgotPasswordError(null)
      setForgotPasswordLoading(true)

      try {
        await sendPasswordResetEmail(auth, forgotPasswordEmail)
        setForgotPasswordSuccess(true)
      } catch (error: any) {
        console.error('Password reset error:', error)

        // Handle specific Firebase errors
        switch (error.code) {
          case 'auth/user-not-found':
            setForgotPasswordError('No account found with this email address.')
            break
          case 'auth/invalid-email':
            setForgotPasswordError('Please enter a valid email address.')
            break
          case 'auth/too-many-requests':
            setForgotPasswordError(
              'Too many password reset attempts. Please try again later.',
            )
            break
          default:
            setForgotPasswordError(
              'Failed to send password reset email. Please try again.',
            )
        }
      } finally {
        setForgotPasswordLoading(false)
      }
    },
    [forgotPasswordEmail],
  )

  const handleBackToLogin = useCallback(() => {
    setShowForgotPassword(false)
    setForgotPasswordEmail('')
    setForgotPasswordError(null)
    setForgotPasswordSuccess(false)
  }, [])

  const handleMfaVerify = useCallback(async () => {
    if (!mfaResolver || mfaCode.length !== 6) return

    setMfaError(null)
    setMfaLoading(true)
    try {
      const totpHint = mfaResolver.hints.find(
        (h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID,
      )
      if (!totpHint) {
        setMfaError('No authenticator app found. Please contact support.')
        return
      }
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(
        totpHint.uid,
        mfaCode,
      )
      await mfaResolver.resolveSignIn(assertion)
      setShowMfaChallenge(false)
      setMfaResolver(null)
      router.navigate({ to: redirectTo })
    } catch (err: any) {
      if (err.code === 'auth/invalid-verification-code') {
        setMfaError('Invalid code. Please try again.')
      } else {
        setMfaError('Verification failed. Please try again.')
      }
      setMfaCode('')
    } finally {
      setMfaLoading(false)
    }
  }, [mfaResolver, mfaCode, router, redirectTo])

  const handleMfaCancel = useCallback(() => {
    setShowMfaChallenge(false)
    setMfaResolver(null)
    setMfaCode('')
    setMfaError(null)
  }, [])

  const linkingProviderName = linkingState.isLinking
    ? (getProviderConfig(linkingState.providerId)?.name ?? 'Unknown')
    : ''

  const error = linkingState.isLinking
    ? linkingError
    : [oauthError?.error, errorEmail].find(
        (e) => e && (e as any).code !== 'auth/multi-factor-auth-required',
      ) || null

  // Log error messages to console instead of showing in the UI,
  // show a user friendly error message instead of technical error message in the UI
  useEffect(() => {
    if (error && error.message) {
      console.error('Login error:', error.message)
    }
  }, [error])

  const loading =
    linkingLoading ||
    loadingProviderId !== null ||
    loadingEmail ||
    forgotPasswordLoading

  return (
    <div className="flex h-screen w-screen flex-col overscroll-none md:flex-row">
      <main className="bg-background flex h-full w-full flex-1 flex-col items-center justify-center px-4">
        <div className="flex w-[350px] flex-col items-start gap-8">
          <img src="/assets/logo.svg" alt="Logo" className="h-15" />
          <div className="flex w-full flex-col gap-1">
            {showMfaChallenge ? (
              <>
                <h1 className="text-2xl font-semibold">
                  Two Factor Authentication
                </h1>
                <span className="text-copy-secondary text-base font-normal">
                  Enter the code from your two-factor authentication app or
                  browser extension below.
                </span>
              </>
            ) : linkingState.isLinking ? (
              <>
                <div className="flex items-center gap-2">
                  <LinkIcon className="h-5 w-5 text-blue-600" />
                  <h1 className="text-2xl font-semibold">Link Your Account</h1>
                </div>
                <span className="text-copy-secondary text-base font-normal">
                  Sign in with your existing password to link your{' '}
                  {linkingProviderName} account
                </span>
              </>
            ) : showForgotPassword ? (
              <>
                <h1 className="text-2xl font-semibold">
                  Forgot your password?
                </h1>
                <span className="text-copy-secondary text-base font-normal">
                  No worries! Enter your email and we'll send you a reset link.
                </span>
              </>
            ) : (
              <>
                <h1 className="text-2xl font-semibold">
                  Welcome back to LineWise!
                </h1>
                <span className="text-copy-secondary text-base font-normal">
                  Sign in to your account to continue
                </span>
              </>
            )}
          </div>
          <div className="w-full space-y-4">
            {!showMfaChallenge &&
              !linkingState.isLinking &&
              !showForgotPassword && (
                <>
                  {prominentProviders.map((config) => (
                    <Button
                      key={config.id}
                      variant="outline"
                      onClick={() => handleSignInWithProvider(config)}
                      className="w-full"
                      disabled={loading}
                    >
                      {loadingProviderId === config.id ? (
                        <Loader2 className="text-primary h-4 w-4 animate-spin" />
                      ) : config.id === 'google.com' ? (
                        <span className="relative size-4">
                          <img
                            src={config.icon}
                            alt={config.name}
                            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 scale-200"
                          />
                        </span>
                      ) : (
                        <img src={config.icon} alt={config.name} />
                      )}
                      Sign in with {config.name}
                    </Button>
                  ))}

                  {dropdownProviders.length > 0 && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full"
                          disabled={loading}
                        >
                          More sign-in options
                          <ChevronDown className="ml-2 h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="center" className="w-[318px]">
                        {dropdownProviders.map((config) => (
                          <DropdownMenuItem
                            key={config.id}
                            onClick={() => handleSignInWithProvider(config)}
                            disabled={loading}
                            className="cursor-pointer"
                          >
                            <img
                              src={config.icon}
                              alt={config.name}
                              className="mr-2 h-4 w-4"
                            />
                            Sign in with {config.name}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}

                  {/* Passkey sign-in. The button is rendered by the
                      Rust/Dioxus `LoginPasskeyButton` — when
                      WebAuthn is unavailable (Firebase preview channel,
                      no `PublicKeyCredential`, insecure context) the
                      Rust component returns nothing, so the entire row
                      collapses without a placeholder. The
                      `getEmail` ref returns the live React-controlled
                      email value so a typed-but-unsubmitted email
                      narrows the credential picker. */}
                  <LoginPasskeyButton
                    getEmail={() => email}
                    onSignedIn={() => router.navigate({ to: redirectTo })}
                  />

                  <div className="flex w-full items-center gap-2">
                    <Separator className="flex-1" />
                    <span className="text-copy-secondary text-sm font-normal">
                      or
                    </span>
                    <Separator className="flex-1" />
                  </div>
                </>
              )}

            {linkingState.isLinking && (
              <Alert className="border-blue-200 bg-blue-50">
                <LinkIcon className="h-4 w-4 text-blue-600" />
                <AlertTitle className="text-blue-800">
                  Account Linking Required
                </AlertTitle>
                <AlertDescription className="text-blue-700">
                  An account with email{' '}
                  <strong>{linkingState.providerEmail}</strong> already exists.
                  Please sign in with your password to link your{' '}
                  {linkingProviderName} account.
                </AlertDescription>
              </Alert>
            )}

            {showMfaChallenge ? (
              <div className="flex w-full flex-col items-center gap-6">
                <InputOTP
                  maxLength={6}
                  value={mfaCode}
                  onChange={(value) => {
                    setMfaCode(value)
                    setMfaError(null)
                  }}
                  onComplete={handleMfaVerify}
                  pattern={REGEXP_ONLY_DIGITS}
                  disabled={mfaLoading}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
                {mfaError && (
                  <p className="text-destructive text-sm">{mfaError}</p>
                )}
                <div className="flex w-full flex-col gap-2">
                  <Button
                    onClick={handleMfaVerify}
                    disabled={mfaLoading || mfaCode.length !== 6}
                    className="w-full"
                  >
                    {mfaLoading && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Verify
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleMfaCancel}
                    disabled={mfaLoading}
                    className="w-full"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : showForgotPassword ? (
              // Forgot Password Form
              <div className="w-full space-y-4">
                <div className="mb-4 flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleBackToLogin}
                    className="h-auto p-0 text-sm"
                  >
                    <ArrowLeft className="mr-1 h-4 w-4" />
                    Back to login
                  </Button>
                </div>

                {forgotPasswordSuccess ? (
                  <Alert className="border-green-200 bg-green-50">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertTitle className="text-green-800">
                      Password Reset Email Sent
                    </AlertTitle>
                    <AlertDescription className="text-green-700">
                      We've sent a password reset link to{' '}
                      <strong>{forgotPasswordEmail}</strong>. Please check your
                      email and follow the instructions to reset your password.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div className="space-y-2">
                      <h2 className="text-lg font-semibold">
                        Reset your password
                      </h2>
                      <p className="text-copy-secondary text-sm">
                        Enter your email address and we'll send you a link to
                        reset your password.
                      </p>
                    </div>
                    <Input
                      type="email"
                      placeholder="Email"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      disabled={forgotPasswordLoading}
                      required
                    />
                    <Button
                      type="submit"
                      disabled={forgotPasswordLoading || !forgotPasswordEmail}
                      className="w-full"
                    >
                      {forgotPasswordLoading && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      Send Reset Email
                    </Button>
                  </form>
                )}

                {forgotPasswordError && (
                  <Alert variant="destructive">
                    <AlertTriangleIcon className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{forgotPasswordError}</AlertDescription>
                  </Alert>
                )}
              </div>
            ) : showSignInWithEmail || linkingState.isLinking ? (
              <form
                onSubmit={handleSignInWithEmail}
                className="flex w-full flex-col gap-4"
              >
                <Input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => {
                    // Prevent email changes when in linking mode
                    if (!linkingState.isLinking) {
                      setEmail(e.target.value)
                    }
                  }}
                  disabled={linkingState.isLinking}
                  readOnly={linkingState.isLinking}
                  className={linkingState.isLinking ? 'bg-gray-50' : ''}
                />
                <Input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="link"
                    className="text-primary h-auto p-0 text-sm"
                    onClick={() => {
                      setShowForgotPassword(true)
                      setForgotPasswordEmail(email) // Pre-fill with current email
                    }}
                  >
                    Forgot password?
                  </Button>
                </div>
                <Button type="submit" disabled={loading}>
                  {linkingLoading && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {linkingState.isLinking ? 'Link Account' : 'Sign in'}
                </Button>
                {linkingState.isLinking && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancelLinking}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                )}
              </form>
            ) : (
              <Button
                variant="ghost"
                onClick={() => setShowSignInWithEmail(true)}
                className="w-full"
              >
                Sign in with Email
              </Button>
            )}
            {!showMfaChallenge && error && (
              <Alert variant="destructive">
                <AlertTriangleIcon className="h-4 w-4" />
                <AlertTitle>
                  {linkingState.isLinking
                    ? 'Error linking account. Please try again.'
                    : 'Error logging in. Please try again.'}
                </AlertTitle>
                <AlertDescription>
                  {linkingState.isLinking &&
                  'code' in error &&
                  error.code === 'auth/wrong-password'
                    ? 'Invalid password. Please enter the correct password for your existing account.'
                    : 'We encountered an issue signing you in. Please check your credentials and try again, or contact support if the problem persists.'}
                </AlertDescription>
              </Alert>
            )}
          </div>
          <p className="text-copy-secondary text-sm font-normal">
            By signing in, you agree to our{' '}
            <a
              href="/legal/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline"
            >
              Privacy Policy
            </a>
            .
          </p>
          <p className="text-copy-secondary text-sm font-normal">
            Having trouble? Contact us at{' '}
            <a href="mailto:info@linewise.io" className="text-primary">
              info@linewise.io
            </a>
          </p>
        </div>
      </main>
      <div className="bg-background hidden h-full w-full flex-1 md:block">
        <img
          src="/assets/login-img.png"
          alt="Login Background"
          className="h-full w-full object-cover"
        />
      </div>
    </div>
  )
}
